// The tavern → combat FIGHT intro — the tape splice score.
//
// Concept (decided 2026-07-29, see docs/motion-audit-start-flow.md): the tape
// stops dead, splice marks flash at frame centre, and two takes slam together —
// your crew against theirs. The arena resolves out of the splice line as the
// seam opens.
//
// Beat order:
//   stop     the transport halts; a hard freeze and a beat of silence
//   marks    splice ticks flash at the cut
//   cover    the two takes slam inward and meet
//   COMMIT   ← navigation fires here, hidden behind the closed seam
//   slam     impact settles; the two crews read on their halves
//   settle   the seam parts outward onto the arena
//
// THE DESIGN CONSTRAINT THAT SHAPES EVERYTHING HERE
// -------------------------------------------------
// This fires every single round — twelve-plus times a match — where the
// Welcome → Menu transport fires once per session. So it is deliberately about
// half the length (0.9s vs 1.52s), and it earns its weight from the *silence
// beat* before the slam rather than from ornament. A hard stop is what makes a
// short sequence feel big, and it is the property that survives repetition.
//
// It also has a second axis the transport doesn't: familiarity. After round one
// the ornament is dropped and the whole thing compresses, so it stays a
// punctuation mark instead of becoming a toll booth.

import type { MotionTier } from './motion';
import { type Beat, type SequenceScore, scaleSequence, devSpeedFactor } from './sequence';

export interface SpliceScore extends SequenceScore {
  /** The freeze. Its silence is the whole idea; do not fill it. */
  stop: Beat;
  /** Splice ticks at the cut — the one ornament, dropped once familiar. */
  marks: Beat;
  /** Impact and hold, where the two crews read. */
  slam: Beat;

  /** Splice ticks drawn across the seam. 0 when familiar or reduced. */
  markCount: number;
  /** Judder amplitude on the freeze, in px. 0 on reduced. */
  judder: number;
}

const FULL: SpliceScore = {
  tier: 'full',
  stop: { at: 0.0, dur: 0.14 },
  marks: { at: 0.1, dur: 0.16 },
  cover: { at: 0.16, dur: 0.16 },
  commitAt: 0.38,
  slam: { at: 0.3, dur: 0.18 },
  settle: { at: 0.62, dur: 0.28 },
  total: 0.9,
  markCount: 9,
  judder: 6,
  travels: true,
};

const LOW_POWER: SpliceScore = {
  tier: 'lowPower',
  stop: { at: 0.0, dur: 0.1 },
  marks: { at: 0.06, dur: 0.1 },
  cover: { at: 0.12, dur: 0.12 },
  commitAt: 0.28,
  slam: { at: 0.24, dur: 0.14 },
  settle: { at: 0.46, dur: 0.22 },
  total: 0.68,
  markCount: 4,
  judder: 3,
  travels: true,
};

/**
 * Reduced motion. A cut is already the least vestibular transition available,
 * so this variant is close to the real thing: the seam still closes and opens,
 * it just cross-dissolves instead of sliding, and nothing judders.
 */
const REDUCED: SpliceScore = {
  tier: 'reduced',
  stop: { at: 0.0, dur: 0.08 },
  marks: { at: 0.0, dur: 0.0 },
  cover: { at: 0.06, dur: 0.12 },
  commitAt: 0.24,
  slam: { at: 0.18, dur: 0.1 },
  settle: { at: 0.36, dur: 0.18 },
  total: 0.54,
  markCount: 0,
  judder: 0,
  travels: false,
};

/**
 * How much the sequence compresses once the player has seen it. Applied through
 * `scaleSequence`, so the cover/commit/settle invariants survive the squeeze
 * automatically rather than needing a second hand-tuned table per tier.
 */
const FAMILIAR_FACTOR = 0.66;

export interface SpliceOptions {
  /**
   * True from round 2 onward. Drops the splice-mark flourish and compresses the
   * whole sequence — same beats, less ornament.
   */
  familiar?: boolean;
}

export function buildSpliceScore(tier: MotionTier, opts: SpliceOptions = {}): SpliceScore {
  const base = tier === 'reduced' ? REDUCED : tier === 'lowPower' ? LOW_POWER : FULL;

  let score = base;
  if (opts.familiar) {
    score = scaleSequence(base, FAMILIAR_FACTOR);
    score = { ...score, marks: { at: score.marks.at, dur: 0 }, markCount: 0 };
  }

  return scaleSequence(score, devSpeedFactor());
}
